/**
 * Блок «Плановий дохід»: комбо-графік (відпуск MMS / плановий дохід) +
 * ТРАНСПОНОВАНА таблиця (періоди у стовпцях, показники рядками, стовпець
 * «Всього» з середньозваженою ціною) + пояснення. Дизайн-токени, світла/темна.
 */
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { TrendingUp, Send, Tag, Coins, Clock, CalendarDays } from 'lucide-react';
import { PlannedIncomeChart } from './FinComboChart';
import FinHourlyTable from './FinHourlyTable';
import type { FinCol, FinRow } from './FinHourlyTable';
import type { PlannedIncomeBlockData, FinGran } from './overviewTypes';
import { fmtMwh, fmtPrice, fmtNum2 } from './overviewTypes';

const colLabel = (key: string | number, gran: FinGran) =>
  gran === 'hour' ? String(Number(key)).padStart(2, '0') : `${String(key).slice(8, 10)}.${String(key).slice(5, 7)}`;

export default function PlannedIncomeBlock({ data, gran, chartOnly = false }:
  { data: PlannedIncomeBlockData; gran: FinGran; chartOnly?: boolean }) {
  const rows = data.rows;  // усі години 01-24 (або всі дати) — таблиця фіксована
  const { t } = useTranslation();
  const hasData = data.rows.some((r) => (r.releaseMwh ?? 0) > 0 || (r.incomeUah ?? 0) > 0);
  // Година/дата під курсором на графіку — підсвічує й докручує ту саму
  // колонку в таблиці нижче (як на /dispatch/forecast-mms).
  const [activeKey, setActiveKey] = useState<string | number | null>(null);

  // Компактний режим (дашборд): лише графік у скляній плитці, заголовок ВСЕРЕДИНІ
  // (щоб верх віджета вирівнювався з рештою). Без таблиці й пояснень.
  if (chartOnly) {
    // Обрізаємо неактивні краї (ночі без відпуску) — показуємо лише робочі години.
    const act = (r: any) => (r.releaseMwh ?? 0) > 0 || (r.incomeUah ?? 0) > 0;
    let lo = 0, hi = data.rows.length - 1;
    if (gran === 'hour') { while (lo < hi && !act(data.rows[lo])) lo++; while (hi > lo && !act(data.rows[hi])) hi--; }
    const cr = data.rows.slice(lo, hi + 1);
    return (
      <div className="tile p-4 flex flex-col h-full">
        <div className="flex items-center gap-2 mb-2 shrink-0">
          <TrendingUp size={15} strokeWidth={1.75} style={{ color: 'var(--text-muted)' }} />
          <h3 className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>{t('ak_fin_planned_income')} <span className="font-normal" style={{ color: 'var(--text-muted)' }}>· {t('ak_fin_without_vat')}</span></h3>
        </div>
        <div className="flex-1 min-h-0">
          {hasData ? <PlannedIncomeChart rows={cr} gran={gran} compact height="100%" /> : <Empty />}
        </div>
      </div>
    );
  }

  const cols: FinCol[] = [
    ...rows.map((r) => ({ key: String(r.key), label: colLabel(r.key, gran) })),
    { key: 'sum', label: t('ak_fin_total'), strong: true },
  ];
  const tableRows: FinRow[] = [
    { name: t('ak_fin_mms_release_mwh_2'), Icon: Send, cells: [...rows.map((r) => ({ v: fmtMwh(r.releaseMwh) })), { v: fmtMwh(data.totalReleaseMwh) }] },
    { name: gran === 'hour' ? t('ak_fin_price_uah_mwh') : t('ak_fin_weighted_avg_price_uah_mwh'), Icon: Tag, cells: [...rows.map((r) => ({ v: fmtPrice(r.priceUahMwh) })), { v: fmtPrice(data.weightedAvgPriceUahMwh) }] },
    { name: t('ak_fin_planned_income_uah'), Icon: Coins, strong: true, cells: [...rows.map((r) => ({ v: fmtNum2(r.incomeUah) })), { v: fmtNum2(data.totalIncomeUah) }] },
    { name: t('ak_fin_accumulated_uah'), Icon: TrendingUp, color: 'var(--text-muted)', cells: [...rows.map((r) => ({ v: fmtNum2(r.cumulativeUah) })), { v: fmtNum2(rows[rows.length - 1]?.cumulativeUah ?? data.totalIncomeUah) }] },
  ];

  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={17} strokeWidth={1.75} style={{ color: 'var(--text-muted)' }} />
        <h3 className="font-display text-base font-semibold" style={{ color: 'var(--text)' }}>{t('ak_fin_planned_income')} <span className="font-normal" style={{ color: 'var(--text-muted)' }}>· {t('ak_fin_without_vat')}</span></h3>
      </div>

      <div className="tile p-4 mb-3">
        {hasData ? <PlannedIncomeChart rows={data.rows} gran={gran} onActiveKeyChange={setActiveKey} /> : <Empty />}
      </div>

      {rows.length > 0 ? (
        <FinHourlyTable firstLabel={gran === 'hour' ? t('ak_fin_metric_hour') : t('ak_fin_metric_date')} cols={cols} rows={tableRows} minWidth={720} activeColKey={activeKey} cornerIcon={gran === 'hour' ? Clock : CalendarDays} />
      ) : (
        <div className="tile p-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>{t('ak_fin_no_data_for_the_period')}</div>
      )}

      {/* Пояснення показників — безрамковий рядок, В ОДНУ СТРОКУ, ВІДЦЕНТРОВАНИЙ
          під таблицею, з роздільниками «·» (переноситься лише за потреби). */}
      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mt-3 text-[11px]" style={{ color: 'var(--text-dim)' }}>
        <span><b style={{ color: 'var(--text-muted)' }}>{t('ak_fin_mms_release')}</b> {t('ak_fin_the_accepted_mms_release_schedule')}</span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span><b style={{ color: 'var(--text-muted)' }}>{t('ak_fin_sale_price')}</b> {t('ak_fin_per_contract_terms_gt_fixed_formula')}</span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span><b style={{ color: 'var(--text-muted)' }}>{t('ak_fin_planned_income')}</b> ({t('ak_fin_without_vat')}) = {t('ak_fin_release_price')}</span>
        {gran !== 'hour' && <><span style={{ opacity: 0.4 }}>·</span><span><b style={{ color: 'var(--text-muted)' }}>{t('ak_fin_weighted_average')}</b> = {t('ak_fin_release_price_release')}</span></>}
      </div>
    </section>
  );
}

function Empty() {
  const { t } = useTranslation();
  return <div className="flex items-center justify-center text-sm" style={{ height: 240, color: 'var(--text-muted)' }}>{t('ak_fin_no_data_for_the_chart')}</div>;
}
